import React, { useRef, useState } from 'react';
import {
  Bold, Italic, Underline as UnderlineIcon,
  AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Link2, Undo2, Redo2, Eraser,
  Image as ImageIcon, Table as TableIcon, Unlink, Upload, X,
} from 'lucide-react';
import type { ComposerApi } from './useComposerBody';
import { clampFontSizePx, escapeHtmlText } from './composerCore';
// NOTE: emailApi (firebase/auth chain) is imported LAZILY inside the
// upload handler — the toolbar renders on every composer surface and
// must not drag the API client into its module graph.

function ToolbarBtn({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded-md transition-all text-xs ${
        disabled
          ? 'text-slate-300 cursor-not-allowed'
          : active
            ? 'bg-blue-100 text-brand font-bold'
            : 'text-slate-600 hover:bg-slate-100 cursor-pointer'
      }`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-4 bg-slate-200 mx-1 shrink-0" />;
}

const TABLE_HTML =
  '<table border="1" cellpadding="6" cellspacing="0" style="border-collapse: collapse; width: 100%;">' +
  '<thead><tr><th>Header 1</th><th>Header 2</th><th>Header 3</th></tr></thead>' +
  '<tbody>' +
  '<tr><td>Cell</td><td>Cell</td><td>Cell</td></tr>' +
  '<tr><td>Cell</td><td>Cell</td><td>Cell</td></tr>' +
  '</tbody></table>';

/**
 * One shared formatting toolbar for every email composer.
 *
 * Visual mode drives the tiptap editor; HTML/code mode applies the same
 * operations as cursor-aware string transforms on the source. Font size
 * (1–50) and text colour are written as inline `style="..."` so they render
 * in WYSIWYG instantly and survive verbatim in the sent HTML.
 *
 * Link and image insertion use inline popovers (PR-11 §C/§F) — no
 * window.prompt: the link popover prefills from the current selection and
 * can remove a link; the image popover uploads to the public email-assets
 * namespace or takes a URL, with an alt-text field, in both modes.
 */
export function EmailToolbar({ composer }: { composer: ComposerApi }) {
  const { editor, mode, codeOps, disabled } = composer;
  const isVisual = mode === 'visual';
  const [sizeDraft, setSizeDraft] = useState('');
  const [panel, setPanel] = useState<'link' | 'image' | null>(null);

  /* ── Link popover state ── */
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');

  /* ── Image popover state ── */
  const [imageTab, setImageTab] = useState<'upload' | 'url'>('upload');
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState('');
  const imageFileRef = useRef<HTMLInputElement | null>(null);

  const applySizeDraft = () => {
    const n = parseInt(sizeDraft, 10);
    if (Number.isFinite(n)) composer.applyFontSize(clampFontSizePx(n));
    setSizeDraft('');
  };

  /* ── Selection preservation (live-walk Finding 3) ──
   * Snapshot the editor selection as the pointer goes down on the
   * toolbar; if the live selection has collapsed by the time a mark
   * command runs (focus stolen by a re-render/panel), restore the
   * snapshot first so Bold/Italic/Underline hit the intended text
   * instead of landing as a stored mark at a stray cursor. */
  const savedSelectionRef = useRef<{ from: number; to: number } | null>(null);
  const captureSelection = () => {
    if (isVisual && editor) {
      const { from, to } = editor.state.selection;
      if (to > from) savedSelectionRef.current = { from, to };
    }
  };
  const restoreSelection = () => {
    if (!isVisual || !editor || !savedSelectionRef.current) return;
    const { from, to } = editor.state.selection;
    if (from === to) {
      try {
        editor.commands.setTextSelection(savedSelectionRef.current);
      } catch {
        /* selection no longer representable — proceed with the live one */
      }
    }
  };
  const withSelection = (fn: () => void) => () => {
    restoreSelection();
    fn();
  };

  const selectionText = (): string => {
    if (isVisual && editor) {
      const { from, to } = editor.state.selection;
      return from === to ? '' : editor.state.doc.textBetween(from, to, ' ');
    }
    const ta = composer.codeTextareaRef.current;
    if (ta) return ta.value.slice(ta.selectionStart ?? 0, ta.selectionEnd ?? 0);
    return '';
  };

  const openLinkPanel = () => {
    if (panel === 'link') {
      setPanel(null);
      return;
    }
    restoreSelection();
    const sel = selectionText();
    setLinkText(sel);
    // Prefill the URL when the selection already is (or sits inside) a link.
    let existing = '';
    if (isVisual && editor?.isActive('link')) {
      existing = String(editor.getAttributes('link').href || '');
    } else if (sel) {
      const m = sel.match(/<a\b[^>]*href="([^"]*)"/i);
      if (m) existing = m[1];
    }
    setLinkUrl(existing || 'https://');
    setPanel('link');
  };

  const applyLinkFromPanel = () => {
    const url = linkUrl.trim();
    if (!url || url === 'https://') return;
    const sel = selectionText();
    if (sel) {
      if (isVisual) composer.applyLink(url);
      else codeOps.link(url);
    } else if (linkText.trim()) {
      const safeUrl = url.replace(/"/g, '&quot;');
      composer.insertHtmlAtCursor(`<a href="${safeUrl}">${escapeHtmlText(linkText.trim())}</a>`);
    } else {
      if (isVisual) composer.applyLink(url);
      else codeOps.link(url);
    }
    setPanel(null);
  };

  const removeLinkFromPanel = () => {
    composer.removeLink();
    setPanel(null);
  };

  const insertImage = (src: string, alt: string) => {
    if (isVisual && editor) {
      editor.chain().focus().setImage({ src, alt }).run();
    } else {
      const safeSrc = src.replace(/"/g, '&quot;');
      const safeAlt = escapeHtmlText(alt);
      composer.insertHtmlAtCursor(`<img src="${safeSrc}" alt="${safeAlt}" style="max-width: 100%; height: auto;">`);
    }
    setPanel(null);
    setImageUrl('');
    setImageAlt('');
    setImageError('');
  };

  const handleImageFile = async (file: File | undefined) => {
    if (!file) return;
    setImageError('');
    if (!/^image\/(png|jpeg|gif|webp)$/.test(file.type)) {
      setImageError('Please choose a PNG, JPG, GIF or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageError('Image is too large (max 5MB).');
      return;
    }
    setImageUploading(true);
    try {
      const { uploadEmailAsset } = await import('./emailApi');
      const { url } = await uploadEmailAsset(file);
      insertImage(url, imageAlt || file.name.replace(/\.[^.]+$/, ''));
    } catch (err: any) {
      setImageError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setImageUploading(false);
    }
  };

  const insertTable = () => {
    if (isVisual && editor) {
      editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    } else {
      composer.insertHtmlAtCursor(TABLE_HTML);
    }
  };

  const panelInputClass =
    'w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md outline-none focus:border-blue-400 text-slate-700 bg-white';
  const panelPrimaryBtn =
    'px-3 py-1.5 bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-bold rounded-lg transition-all cursor-pointer disabled:opacity-40';

  return (
    <div
      className="flex items-center gap-0.5 flex-wrap"
      aria-label="Email formatting toolbar"
      onMouseDownCapture={captureSelection}
    >
      <ToolbarBtn
        onClick={withSelection(() => (isVisual ? editor?.chain().focus().toggleBold().run() : codeOps.bold()))}
        active={isVisual && !!editor?.isActive('bold')}
        disabled={disabled}
        title="Bold (Ctrl+B)"
      >
        <Bold className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={withSelection(() => (isVisual ? editor?.chain().focus().toggleItalic().run() : codeOps.italic()))}
        active={isVisual && !!editor?.isActive('italic')}
        disabled={disabled}
        title="Italic (Ctrl+I)"
      >
        <Italic className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={withSelection(() => (isVisual ? editor?.chain().focus().toggleUnderline().run() : codeOps.underline()))}
        active={isVisual && !!editor?.isActive('underline')}
        disabled={disabled}
        title="Underline (Ctrl+U)"
      >
        <UnderlineIcon className="h-3.5 w-3.5" />
      </ToolbarBtn>

      <Divider />

      {/* Block style — paragraph / headings (inline tags, email-safe) */}
      <select
        aria-label="Block style"
        title="Paragraph / heading style"
        disabled={disabled}
        value={
          isVisual && editor
            ? editor.isActive('heading', { level: 1 })
              ? '1'
              : editor.isActive('heading', { level: 2 })
                ? '2'
                : editor.isActive('heading', { level: 3 })
                  ? '3'
                  : '0'
            : '0'
        }
        onChange={(e) => {
          const level = Number(e.target.value) as 0 | 1 | 2 | 3;
          if (isVisual) {
            if (!editor) return;
            if (level === 0) editor.chain().focus().setParagraph().run();
            else editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 }).run();
          } else {
            codeOps.heading(level);
          }
        }}
        className="px-1 py-1 text-xs border border-slate-200 rounded-md outline-none focus:border-blue-400 text-slate-700 bg-white cursor-pointer disabled:opacity-50"
      >
        <option value="0">Paragraph</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
      </select>

      <Divider />

      <ToolbarBtn
        onClick={() => (isVisual ? editor?.chain().focus().setTextAlign('left').run() : codeOps.alignLeft())}
        active={isVisual && !!editor?.isActive({ textAlign: 'left' })}
        disabled={disabled}
        title="Align left"
      >
        <AlignLeft className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => (isVisual ? editor?.chain().focus().setTextAlign('center').run() : codeOps.alignCenter())}
        active={isVisual && !!editor?.isActive({ textAlign: 'center' })}
        disabled={disabled}
        title="Align center"
      >
        <AlignCenter className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => (isVisual ? editor?.chain().focus().setTextAlign('right').run() : codeOps.alignRight())}
        active={isVisual && !!editor?.isActive({ textAlign: 'right' })}
        disabled={disabled}
        title="Align right"
      >
        <AlignRight className="h-3.5 w-3.5" />
      </ToolbarBtn>

      <Divider />

      <ToolbarBtn
        onClick={() => (isVisual ? editor?.chain().focus().toggleBulletList().run() : codeOps.bulletList())}
        active={isVisual && !!editor?.isActive('bulletList')}
        disabled={disabled}
        title="Bulleted list"
      >
        <List className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => (isVisual ? editor?.chain().focus().toggleOrderedList().run() : codeOps.orderedList())}
        active={isVisual && !!editor?.isActive('orderedList')}
        disabled={disabled}
        title="Numbered list"
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </ToolbarBtn>

      <Divider />

      {/* Link + Image + Table — popover-driven (no window.prompt) */}
      <div className="relative flex items-center gap-0.5">
        <ToolbarBtn
          onClick={openLinkPanel}
          active={panel === 'link' || (isVisual && !!editor?.isActive('link'))}
          disabled={disabled}
          title="Insert link"
        >
          <Link2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => setPanel(panel === 'image' ? null : 'image')}
          active={panel === 'image'}
          disabled={disabled}
          title="Insert image"
        >
          <ImageIcon className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={insertTable} disabled={disabled} title="Insert table (3×3 with header row)">
          <TableIcon className="h-3.5 w-3.5" />
        </ToolbarBtn>

        {panel === 'link' && (
          <div className="absolute left-0 top-full mt-1.5 z-[8500] w-80 bg-white border border-slate-200 rounded-xl shadow-xl p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-700">Insert link</span>
              <button
                type="button"
                onClick={() => setPanel(null)}
                className="text-slate-400 hover:text-slate-600 cursor-pointer"
                aria-label="Close link panel"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className={panelInputClass}
              aria-label="Link URL"
              autoFocus
            />
            <input
              type="text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              placeholder="Link text (optional — defaults to the URL)"
              className={panelInputClass}
              aria-label="Link text"
            />
            <div className="flex items-center justify-between gap-2 pt-0.5">
              <button
                type="button"
                onClick={removeLinkFromPanel}
                className="px-2.5 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-50 rounded-lg flex items-center gap-1 cursor-pointer"
                title="Remove the link around the current selection (keeps the text)"
              >
                <Unlink className="h-3.5 w-3.5" />
                Remove link
              </button>
              <button
                type="button"
                onClick={applyLinkFromPanel}
                disabled={!linkUrl.trim() || linkUrl.trim() === 'https://'}
                className={panelPrimaryBtn}
              >
                Insert link
              </button>
            </div>
          </div>
        )}

        {panel === 'image' && (
          <div className="absolute left-0 top-full mt-1.5 z-[8500] w-80 bg-white border border-slate-200 rounded-xl shadow-xl p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-700">Insert image</span>
              <button
                type="button"
                onClick={() => setPanel(null)}
                className="text-slate-400 hover:text-slate-600 cursor-pointer"
                aria-label="Close image panel"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
              {(['upload', 'url'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setImageTab(tab)}
                  className={`flex-1 px-2 py-1 text-xs font-bold rounded-md transition-all cursor-pointer ${
                    imageTab === tab ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab === 'upload' ? 'Upload' : 'From URL'}
                </button>
              ))}
            </div>
            {imageTab === 'upload' ? (
              <div>
                <input
                  ref={imageFileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    void handleImageFile(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => imageFileRef.current?.click()}
                  disabled={imageUploading}
                  className="w-full px-3 py-2 border-2 border-dashed border-slate-200 hover:border-blue-300 rounded-lg text-xs font-bold text-slate-600 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  <Upload className="h-4 w-4" />
                  {imageUploading ? 'Uploading…' : 'Choose an image (PNG, JPG, GIF, WebP — max 5MB)'}
                </button>
                <p className="text-[11px] text-slate-500 mt-1">
                  Uploaded images are hosted publicly so email recipients can see them.
                </p>
              </div>
            ) : (
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://…/image.png"
                className={panelInputClass}
                aria-label="Image URL"
              />
            )}
            <input
              type="text"
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
              placeholder="Alt text (for screen readers & blocked images)"
              className={panelInputClass}
              aria-label="Image alt text"
            />
            {imageError && <p className="text-[11px] font-semibold text-rose-600">{imageError}</p>}
            {imageTab === 'url' && (
              <div className="flex justify-end pt-0.5">
                <button
                  type="button"
                  onClick={() => imageUrl.trim() && insertImage(imageUrl.trim(), imageAlt)}
                  disabled={!imageUrl.trim()}
                  className={panelPrimaryBtn}
                >
                  Insert image
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <Divider />

      <ToolbarBtn
        onClick={() => (isVisual ? editor?.chain().focus().undo().run() : codeOps.undo())}
        disabled={disabled}
        title="Undo (Ctrl+Z)"
      >
        <Undo2 className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => (isVisual ? editor?.chain().focus().redo().run() : codeOps.redo())}
        disabled={disabled}
        title="Redo (Ctrl+Y)"
      >
        <Redo2 className="h-3.5 w-3.5" />
      </ToolbarBtn>

      <Divider />

      {/* Font size 1–50 → inline style="font-size:Npx" */}
      <div className="flex items-center gap-1 px-1" title="Font size (1–50px)">
        <span className="text-[10px] font-bold text-slate-400 select-none">A</span>
        <input
          type="number"
          min={1}
          max={50}
          value={sizeDraft}
          placeholder={composer.currentFontSize != null ? String(composer.currentFontSize) : 'Size'}
          onChange={(e) => setSizeDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              applySizeDraft();
            }
          }}
          onBlur={applySizeDraft}
          disabled={disabled}
          className="w-12 px-1 py-1 text-xs border border-slate-200 rounded-md outline-none focus:border-blue-400 text-slate-700"
          aria-label="Font size in pixels (1 to 50)"
        />
        <span className="text-[11px] text-slate-500 select-none">px</span>
      </div>

      {/* Text colour → inline style="color:..." */}
      <div className="flex items-center gap-1 px-1" title="Text colour">
        <input
          type="color"
          value={composer.currentColor || '#000000'}
          onChange={(e) => composer.applyColor(e.target.value)}
          disabled={disabled}
          className="w-6 h-6 cursor-pointer rounded border border-slate-200 p-0 bg-transparent"
          aria-label="Text colour"
        />
      </div>

      <Divider />

      <ToolbarBtn
        onClick={() => {
          if (isVisual) {
            editor?.chain().focus().unsetAllMarks().clearNodes().unsetColor().unsetFontSize().run();
          } else {
            codeOps.clearFormatting();
          }
        }}
        disabled={disabled}
        title="Clear formatting"
      >
        <Eraser className="h-3.5 w-3.5" />
      </ToolbarBtn>
    </div>
  );
}
